import React, { useState, useEffect, useRef } from 'react';
import { Bell, CheckCheck, Sparkles, AlertCircle, Info, X, RefreshCw, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, type AppNotification } from '../../hooks/useNotifications';
import { useTranslation } from 'react-i18next';

interface NotificationBellProps {
  userId: string | undefined;
  onJobCompleted?: (jobId: string, gameName: string) => void;
}

function timeAgo(dateStr: string, t: (key: string, options?: any) => string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('notifications.just_now');
  if (mins < 60) return t('notifications.minutes_ago', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('notifications.hours_ago', { count: hrs });
  return t('notifications.days_ago', { count: Math.floor(hrs / 24) });
}

const ICON_MAP: Record<string, typeof Info> = {
  ai_generation_complete: Sparkles,
  ai_generation_failed: AlertCircle,
  info: Info,
  voucher_sync_complete: RefreshCw,
  voucher_sync_failed: AlertTriangle,
};

const COLOR_MAP: Record<string, string> = {
  ai_generation_complete: 'text-emerald-500',
  ai_generation_failed: 'text-red-500',
  info: 'text-blue-500',
  voucher_sync_complete: 'text-emerald-500',
  voucher_sync_failed: 'text-red-500',
};

const BG_MAP: Record<string, string> = {
  ai_generation_complete: 'bg-emerald-50 dark:bg-emerald-900/20',
  ai_generation_failed: 'bg-red-50 dark:bg-red-900/20',
  info: 'bg-blue-50 dark:bg-blue-900/20',
  voucher_sync_complete: 'bg-emerald-50 dark:bg-emerald-900/20',
  voucher_sync_failed: 'bg-red-50 dark:bg-red-900/20',
};

function NotificationItem({
  notification,
  onMarkRead,
  onJobClick,
  onNavigate,
}: {
  notification: AppNotification;
  onMarkRead: (id: string) => void;
  onJobClick?: (jobId: string, gameName: string) => void;
  onNavigate?: (path: string) => void;
}) {
  const { t } = useTranslation();
  const Icon = ICON_MAP[notification.type] || Info;
  const iconColor = COLOR_MAP[notification.type] || 'text-blue-500';
  const bgColor = BG_MAP[notification.type] || 'bg-blue-50 dark:bg-blue-900/20';

  const handleClick = () => {
    if (!notification.read) onMarkRead(notification.id);
    if (notification.type === 'ai_generation_complete' && notification.metadata?.job_id && onJobClick) {
      onJobClick(
        notification.metadata.job_id as string,
        (notification.metadata.game_name as string) || 'your game'
      );
    } else if (notification.action_url && onNavigate) {
      onNavigate(notification.action_url);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full text-left px-4 py-3.5 flex items-start gap-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
        !notification.read ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''
      }`}
    >
      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${bgColor}`}>
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium leading-snug ${notification.read ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
            {notification.title}
          </p>
          {!notification.read && (
            <span className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-blue-500" />
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed line-clamp-2">
          {notification.message}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          {timeAgo(notification.created_at, t)}
        </p>
      </div>
    </button>
  );
}

const NotificationBell: React.FC<NotificationBellProps> = ({ userId, onJobCompleted }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead, isLoading } = useNotifications(userId);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const prevUnreadRef = useRef(unreadCount);

  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      const latest = notifications.find((n) => !n.read);
      if (latest) {
        triggerPulse();
      }
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount, notifications]);

  const [pulse, setPulse] = useState(false);
  const triggerPulse = () => {
    setPulse(true);
    setTimeout(() => setPulse(false), 1000);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleJobClick = (jobId: string, gameName: string) => {
    setOpen(false);
    onJobCompleted?.(jobId, gameName);
  };

  const handleNavigate = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className={`relative p-2 rounded-lg transition-colors text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 ${
          pulse ? 'animate-bounce' : ''
        }`}
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-0.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{t('notifications.title')}</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={t('notifications.mark_all_read')}
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  <span>{t('notifications.all_read')}</span>
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
            {isLoading ? (
              <div className="px-4 py-8 flex flex-col items-center gap-2">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-xs text-gray-400">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-10 flex flex-col items-center gap-2 text-center">
                <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <Bell className="h-5 w-5 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('notifications.no_notifications')}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {t('notifications.ai_generation_hint')}
                </p>
              </div>
            ) : (
              notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onMarkRead={markAsRead}
                  onJobClick={handleJobClick}
                  onNavigate={handleNavigate}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
